import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import SvgIcon from '@/components/SvgIcon';
import { useCoachCommissionTables, usePatchCoachCommissionTables } from '@/features/coachCommissionTable/hooks';
import { ScheduleBracket, CoachCommissionTableBody } from '@/features/coachCommissionTable/types';
import { useCoachWorkRoutines } from '@/features/coachWorkRoutine';
import { CoachWorkRoutine } from '@/features/coachWorkRoutine/types';
import { cloneDeep } from 'lodash';
import { colors } from '@/lib/colors';
import { ToggleSwitch } from '../ModalCommission';
import { BracketInput, RemoveButton, CoachListPanel } from './BracketPrimitives';
import ExceptionRatesSummary from './exceptionRates/ExceptionRatesSummary';
import { useExceptionRatesEditor } from './exceptionRates/useExceptionRatesEditor';
import NoshowAlwaysInSetting from './NoshowAlwaysInSetting';
import { isNoshowAlwaysInActiveFor, zeroableReasonLabel } from '@/features/noshowAlwaysIn';

export interface DetailedCommissionTabRef {
    handleApply: () => Promise<void>;
    handleApplyToAll: () => void;
}

type Props = {
    centerId?: string;
    year: string;
    month: string;
    onIsModifiedChange?: (isModified: boolean) => void;
};

const DetailedCommissionTab = forwardRef<DetailedCommissionTabRef, Props>(
    ({ centerId, year, month, onIsModifiedChange }, ref) => {
        const { data: commissionData, isLoading } = useCoachCommissionTables({
            centerId: centerId || '',
            year,
            month,
            enabled: !!centerId,
        });

        const { data: routinesData } = useCoachWorkRoutines({
            centerId: centerId || '',
            year,
            month,
            enabled: !!centerId,
        });

        const hasNoWorkRoutine = useCallback(
            (coachId: string): boolean => {
                if (!routinesData?.coachWorkRoutines) return false;
                const coach = routinesData.coachWorkRoutines.find(
                    (c: CoachWorkRoutine) => c.coachId === coachId,
                );
                if (!coach?.weeklyRoutine) return true;
                return coach.weeklyRoutine.every((day) => !day.isWorking);
            },
            [routinesData],
        );

        const patchCommission = usePatchCoachCommissionTables();

        /**
         * 예외 수업료 — 구간 요율(localData)과 따로 들고, 전용 PUT 으로 강사 1명씩 저장한다.
         * 기존 「적용하기」(PATCH, 전원 전송)에는 싣지 않는다. 입구 노출은 편집기(isExceptionRatesVisibleInMode)가 정한다.
         */
        const exceptionEditor = useExceptionRatesEditor({ centerId, year, month, settlementMode: 'tier' });

        /**
         * 「노쇼 수업은 항상 IN」 — 서버 능력 신호(exceptionRatesInMultiplier + noshowAlwaysInFromMonth 키)가 있을 때만 보인다.
         * 저장 뒤 요율 목록은 다시 읽지 않으므로(편집 중인 구간표 보호) 응답값을 여기 덮어 두고, 새 조회가 오면 버린다.
         */
        const noshowCapable =
            commissionData?.exceptionRatesInMultiplier === true &&
            !!commissionData &&
            'noshowAlwaysInFromMonth' in commissionData;
        const [savedNoshowFromMonth, setSavedNoshowFromMonth] = useState<{ value: string | null } | null>(null);
        useEffect(() => setSavedNoshowFromMonth(null), [commissionData]);
        const noshowFromMonth = savedNoshowFromMonth
            ? savedNoshowFromMonth.value
            : commissionData?.noshowAlwaysInFromMonth ?? null;
        const noshowActiveThisMonth = noshowCapable && isNoshowAlwaysInActiveFor(noshowFromMonth, year, month);

        // Local state to hold the editable tabular data
        const [localData, setLocalData] = useState<CoachCommissionTableBody[]>([]);
        const [initialData, setInitialData] = useState<CoachCommissionTableBody[]>([]);
        const [selectedCoachId, setSelectedCoachId] = useState<string | null>(null);

        useEffect(() => {
            if (commissionData?.coachCommissionTables) {
                const initialDataValue = commissionData.coachCommissionTables.map((coach: any) => ({
                    coachId: coach.coachId,
                    coachName: coach.coachName,
                    commissionType: coach.commissionType || 'percentage',
                    inScheduleBrackets:
                        coach.inScheduleBrackets?.length > 0
                            ? cloneDeep(coach.inScheduleBrackets)
                            : [{ minRevenue: 0, maxRevenue: null, commissionRate: 0 }],
                    outScheduleBrackets:
                        coach.outScheduleBrackets?.length > 0
                            ? cloneDeep(coach.outScheduleBrackets)
                            : [{ minRevenue: 0, maxRevenue: null, commissionRate: 0 }],
                }));
                setLocalData(initialDataValue);
                setInitialData(cloneDeep(initialDataValue));

                setSelectedCoachId((prev) => {
                    if (!prev && initialDataValue.length > 0) {
                        return initialDataValue[0].coachId;
                    }
                    return prev;
                });
            }
        }, [commissionData]);

        const isCoachModified = useCallback(
            (id: string) => {
                const current = localData.find((c) => c.coachId === id);
                const initial = initialData.find((c) => c.coachId === id);
                if (!current || !initial) return false;

                const stripId = (bracket: any) => {
                    const { _id, ...rest } = bracket;
                    return rest;
                };

                const currentIn = current.inScheduleBrackets.map(stripId);
                const currentOut = current.outScheduleBrackets.map(stripId);
                const initialIn = initial.inScheduleBrackets.map(stripId);
                const initialOut = initial.outScheduleBrackets.map(stripId);

                return (
                    current.commissionType !== initial.commissionType ||
                    JSON.stringify(currentIn) !== JSON.stringify(initialIn) ||
                    JSON.stringify(currentOut) !== JSON.stringify(initialOut)
                );
            },
            [localData, initialData],
        );

        useEffect(() => {
            if (!onIsModifiedChange) return;
            const isAnyCoachModified = localData.some((c) => isCoachModified(c.coachId));
            onIsModifiedChange(isAnyCoachModified);
        }, [localData, isCoachModified, onIsModifiedChange]);

        useImperativeHandle(ref, () => ({
            handleApply: async () => {
                if (!centerId) return;
                const stripId = (bracket: any) => {
                    const { _id, ...rest } = bracket;
                    return rest;
                };

                const bodyArray = localData.map(({ coachId, commissionType, inScheduleBrackets, outScheduleBrackets }) => ({
                    coachId,
                    commissionType,
                    inScheduleBrackets: inScheduleBrackets.map(stripId),
                    outScheduleBrackets: outScheduleBrackets.map(stripId),
                }));
                await patchCommission.mutateAsync({
                    centerId,
                    body: {
                        year,
                        month,
                        coachCommissionTables: bodyArray,
                    },
                });
            },
            handleApplyToAll: () => {
                if (!selectedCoachId) return;
                const selectedData = localData.find((c) => c.coachId === selectedCoachId);
                if (!selectedData) return;

                setLocalData((prev) =>
                    prev.map((coach) => ({
                        ...coach,
                        commissionType: selectedData.commissionType,
                        inScheduleBrackets: cloneDeep(selectedData.inScheduleBrackets),
                        outScheduleBrackets: cloneDeep(selectedData.outScheduleBrackets),
                    })),
                );
            },
        }));

        const handleAddBracket = (type: 'in' | 'out') => {
            setLocalData((prev) => {
                const next = cloneDeep(prev);
                const coachIndex = next.findIndex((c) => c.coachId === selectedCoachId);
                if (coachIndex === -1) return prev;
                const targetArray =
                    type === 'in' ? next[coachIndex].inScheduleBrackets : next[coachIndex].outScheduleBrackets;

                // Get last bracket's maxRevenue to set as new bracket's minRevenue
                const lastBracket = targetArray[targetArray.length - 1];
                const newMin = lastBracket && lastBracket.maxRevenue ? lastBracket.maxRevenue : 0;
                targetArray.push({ minRevenue: newMin, maxRevenue: null, commissionRate: 0 });
                return next;
            });
        };

        const handleRemoveBracket = (type: 'in' | 'out', index: number) => {
            setLocalData((prev) => {
                const next = cloneDeep(prev);
                const coachIndex = next.findIndex((c) => c.coachId === selectedCoachId);
                if (coachIndex === -1) return prev;
                const targetArray =
                    type === 'in' ? next[coachIndex].inScheduleBrackets : next[coachIndex].outScheduleBrackets;

                targetArray.splice(index, 1);
                if (targetArray.length === 0) {
                    targetArray.push({ minRevenue: 0, maxRevenue: null, commissionRate: 0 });
                }
                return next;
            });
        };

        const handleChangeBracket = (
            type: 'in' | 'out',
            index: number,
            field: keyof ScheduleBracket,
            value: number | null,
        ) => {
            setLocalData((prev) => {
                const next = cloneDeep(prev);
                const coachIndex = next.findIndex((c) => c.coachId === selectedCoachId);
                if (coachIndex === -1) return prev;
                const targetArray =
                    type === 'in' ? next[coachIndex].inScheduleBrackets : next[coachIndex].outScheduleBrackets;

                targetArray[index][field] = value as never;
                return next;
            });
        };

        const selectedCoachData = localData.find((c) => c.coachId === selectedCoachId);

        /**
         * 이 강사의 이 달 IN 구간표로 0원이 될 수 있는가(서버 inRateZeroable, 저장된 표 기준) — 시간과 상관없이 IN 으로 계산하는
         * 수업(노쇼 설정이 켜진 달, 또는 「항상 IN 요율의 배수」 예외)이 있을 때만 알린다.
         */
        const zeroableReasonOf = useCallback(
            (coachId: string) => {
                if (!noshowCapable) return null;
                const reason = commissionData?.coachCommissionTables?.find((c) => c.coachId === coachId)?.inRateZeroable;
                if (!reason) return null;
                const hasInMultiplierRule =
                    exceptionEditor.visible && exceptionEditor.rulesOf(coachId).some((rule) => rule.mode === 'inMultiplier');
                return noshowActiveThisMonth || hasInMultiplierRule ? reason : null;
            },
            [noshowCapable, commissionData, exceptionEditor, noshowActiveThisMonth],
        );
        const selectedZeroableReason = selectedCoachId ? zeroableReasonOf(selectedCoachId) : null;

        const handleToggleCommissionType = (value: 'percentage' | 'fixed') => {
            if (!selectedCoachId) return;
            setLocalData((prev) => {
                const next = cloneDeep(prev);
                const coachIndex = next.findIndex((c) => c.coachId === selectedCoachId);
                if (coachIndex === -1) return prev;
                next[coachIndex].commissionType = value;
                // 토글 변경 시 모든 bracket의 commissionRate를 0으로 초기화
                next[coachIndex].inScheduleBrackets.forEach((b) => (b.commissionRate = 0));
                next[coachIndex].outScheduleBrackets.forEach((b) => (b.commissionRate = 0));
                return next;
            });
        };

        const commissionType = selectedCoachData?.commissionType ?? 'percentage';
        const rateUnit = commissionType === 'percentage' ? '%' : '원';
        const rateLabel = commissionType === 'percentage' ? '적용 요율' : '적용 정산액';

        const renderBracketRows = (brackets: ScheduleBracket[], type: 'in' | 'out') => {
            return (
                <Col className="gap-[16px]">
                    {brackets.map((bracket, index) => (
                        <Row key={index} className="items-center">
                            <BracketInput
                                value={bracket.minRevenue}
                                onChange={(val: number | null) => handleChangeBracket(type, index, 'minRevenue', val)}
                                unit="만원"
                                width={178}
                            />
                            <RegularText className="mx-[12px] text-[#6B7280]">~</RegularText>
                            <BracketInput
                                value={bracket.maxRevenue}
                                onChange={(val: number | null) => handleChangeBracket(type, index, 'maxRevenue', val)}
                                unit="만원"
                                placeholder=""
                                width={178}
                            />

                            <div className="w-[12px]" />

                            <BracketInput
                                value={bracket.commissionRate}
                                onChange={(val: number | null) => {
                                    if (val !== null) {
                                        if (commissionType === 'percentage' && val > 100) return;
                                        if (commissionType === 'fixed' && String(val).length > 7) return;
                                    }
                                    handleChangeBracket(type, index, 'commissionRate', val);
                                }}
                                unit={rateUnit}
                                width={178}
                            />

                            <RemoveButton onClick={() => handleRemoveBracket(type, index)} />
                        </Row>
                    ))}
                </Col>
            );
        };

        if (isLoading) return <div className="p-4">Loading...</div>;

        return (
            <>
            {noshowCapable && centerId && (
                <NoshowAlwaysInSetting
                    centerId={centerId}
                    fromMonth={noshowFromMonth}
                    readOnly={exceptionEditor.readOnly}
                    onSaved={(value) => setSavedNoshowFromMonth({ value })}
                />
            )}
            <Row className="flex flex-row w-full mt-[10px]">
                {/* 왼쪽 강사 리스트 */}
                <CoachListPanel
                    coaches={localData.map((c: any) => ({ coachId: c.coachId, coachName: c.coachName }))}
                    selectedCoachId={selectedCoachId}
                    onSelect={setSelectedCoachId}
                    isCoachModified={isCoachModified}
                    {...(noshowCapable ? { isCoachWarned: (coachId: string) => !!zeroableReasonOf(coachId) } : {})}
                />

                {/* 구분선 */}
                <div className="w-[0.5px] bg-[#E6E8EA] mx-[20px]" />

                {/* 오른쪽 컨텐츠 */}
                {selectedCoachData && (
                    <Col className="flex flex-col overflow-y-scroll pr-[16px]">
                        {/* 정률제/정액제 토글 */}
                        <Row className="items-center mb-[24px] gap-[12px]">
                            <MediumText fontSize={16} color="#3D4244">
                                정률제/정액제 선택
                            </MediumText>
                            <ToggleSwitch
                                value={commissionType}
                                onToggle={handleToggleCommissionType}
                            />
                        </Row>

                        {/* 근무시간 미설정 안내 */}
                        {selectedCoachId && hasNoWorkRoutine(selectedCoachId) && (
                            <Row className="items-center mb-[20px] px-[16px] py-[12px] bg-[#FFF8E1] rounded-[10px] gap-[8px]">
                                <SvgIcon name="SvgWarningInCircle" size={17} fill={'#F59E0B'} />
                                <RegularText fontSize={14} lineHeight={20} color="#92400E">
                                    근무 시간이 설정되지 않아 모든 수업이 근무 시간 내(IN)로 처리됩니다.
                                </RegularText>
                            </Row>
                        )}

                        {selectedZeroableReason && (
                            <Row
                                role="note"
                                className="items-center mb-[20px] px-[16px] py-[12px] bg-[#FFF8E1] rounded-[10px] gap-[8px]"
                            >
                                <SvgIcon name="SvgWarningInCircle" size={17} fill={'#F59E0B'} />
                                <RegularText fontSize={14} lineHeight={20} color="#92400E">
                                    {`매출 구간에 따라 IN 요율이 0일 수 있어요(${zeroableReasonLabel(selectedZeroableReason)}). 그 구간에서는 시간과 상관없이 IN 요율로 계산하는 수업이 0원이 돼요(개별 요율·예외 금액 수업은 그대로).`}
                                </RegularText>
                            </Row>
                        )}

                        {/* IN 수업료 섹션 */}
                        <Col className="mb-[92px]">
                            <Row className="items-center mb-[20px] gap-[8px]">
                                <MediumText fontSize={18} color="#3D4244">
                                    근무 시간 내 수업료
                                </MediumText>
                                <div className="w-[44px] h-[26px]  bg-[#E6F2EC] rounded-[13px] flex items-center justify-center">
                                    <RegularText fontSize={13} letterSpacing={-0.5} color="#12A25D">
                                        IN
                                    </RegularText>
                                </div>
                            </Row>

                            <Row className="mb-[12px]">
                                <div className="w-[244px]">
                                    <DemiLightText fontSize={14} color="#6B7280">
                                        매출 구간 (이상~미만)
                                    </DemiLightText>
                                </div>
                                <div className="w-[150px]" />
                                <div className="w-[110px]">
                                    <DemiLightText fontSize={14} color="#6B7280">
                                        {rateLabel}
                                    </DemiLightText>
                                </div>
                            </Row>

                            {renderBracketRows(selectedCoachData.inScheduleBrackets, 'in')}

                            <Row
                                className="mt-[16px] items-center cursor-pointer gap-[8px]"
                                onClick={() => handleAddBracket('in')}
                            >
                                <SvgIcon name="SvgPlus" size={16} fill={colors.primary} />
                                <RegularText fontSize={16} color={colors.primary}>
                                    추가하기
                                </RegularText>
                            </Row>
                        </Col>

                        {/* OUT 수업료 섹션 */}
                        <Col className="mb-[40px]">
                            <Row className="items-center mb-[20px] gap-[8px]">
                                <MediumText fontSize={18} color="#3D4244">
                                    근무 시간 외 수업료
                                </MediumText>
                                <div className="w-[44px] h-[26px] bg-[#FEEAEB] rounded-[13px] flex items-center justify-center">
                                    <RegularText fontSize={13} letterSpacing={-0.5} color="#D93043">
                                        OUT
                                    </RegularText>
                                </div>
                            </Row>

                            <Row className="mb-[12px]">
                                <div className="w-[244px]">
                                    <DemiLightText fontSize={14} color="#6B7280">
                                        매출 구간 (이상~미만)
                                    </DemiLightText>
                                </div>
                                <div className="w-[150px]" />
                                <div className="w-[110px]">
                                    <DemiLightText fontSize={14} color="#6B7280">
                                        {rateLabel}
                                    </DemiLightText>
                                </div>
                            </Row>

                            {renderBracketRows(selectedCoachData.outScheduleBrackets, 'out')}

                            <Row
                                className="mt-[16px] items-center cursor-pointer gap-[8px]"
                                onClick={() => handleAddBracket('out')}
                            >
                                <SvgIcon name="SvgPlus" size={16} fill={colors.primary} />
                                <RegularText fontSize={16} color={colors.primary}>
                                    추가하기
                                </RegularText>
                            </Row>
                        </Col>

                        {/*
                          예외 수업료 — 구간표 다음에 읽히게 맨 아래에 둔다.
                          tier 가 아니거나(effective=false) 구서버면 아무것도 그리지 않는다.
                          규칙이 없으면 기존 「추가하기」와 같은 한 줄 링크만, 있을 때만 요약을 보인다.
                        */}
                        {exceptionEditor.visible && selectedCoachId && exceptionEditor.hasCoach(selectedCoachId) && (
                            <ExceptionRatesSummary
                                rules={exceptionEditor.rulesOf(selectedCoachId)}
                                readOnly={exceptionEditor.readOnly}
                                categoryName={exceptionEditor.categoryName}
                                settlementMode="tier"
                                onOpen={() => exceptionEditor.open(selectedCoachId)}
                            />
                        )}
                    </Col>
                )}

                {exceptionEditor.dialogs}
            </Row>
            </>
        );
    },
);

export default DetailedCommissionTab;
